import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTour } from '../contexts/TourContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { 
  Wrench, Plus, Trash2, MapPin, Activity, AlertCircle, 
  Copy, ExternalLink, History, ChevronRight, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function MaintenanceEquipmentPanel({ 
  isOpen, 
  onClose, 
  currentSweepId,
  pointerIntersection,
  onNavigateToEquipment 
}) {
  const { token, isAdmin } = useAuth();
  const { currentTour } = useTour();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [history, setHistory] = useState([]);
  const [prediction, setPrediction] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    equipment_id: '',
    equipment_name: '',
    equipment_type: 'punch',
    max_cycles_before_inspection: 100000,
    max_cycles_before_replacement: 500000,
  });
  
  // Fetch equipment for current tour
  useEffect(() => {
    const fetchEquipment = async () => {
      if (!currentTour?.id || !token) return;
      
      setLoading(true);
      try {
        // Use XMLHttpRequest to avoid body stream issues
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${API_URL}/api/maintenance/equipment/tour/${currentTour.id}`, false); // Synchronous for simplicity
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send();
        
        if (xhr.status >= 200 && xhr.status < 300) {
          const data = JSON.parse(xhr.responseText);
          setEquipment(data);
        } else {
          console.error('Failed to fetch equipment:', xhr.status, xhr.responseText);
        }
      } catch (err) {
        console.error('Failed to fetch equipment:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      fetchEquipment();
    }
  }, [isOpen, currentTour?.id, token]);
  
  const handleCreate = async () => {
    if (!formData.equipment_id || !formData.equipment_name) {
      alert('Please enter Equipment ID and Name');
      return;
    }
    
    if (!currentTour?.id) {
      alert('No tour selected. Please select a tour first.');
      return;
    }
    
    if (!token) {
      alert('Not authenticated. Please log in again.');
      return;
    }
    
    const payload = {
      ...formData,
      tour_id: currentTour.id,
      sweep_id: currentSweepId || null,
      position: pointerIntersection?.position || null,
    };
    
    console.log('Creating equipment with payload:', JSON.stringify(payload));
    console.log('Using token:', token ? 'Token exists' : 'NO TOKEN');
    
    try {
      const response = await fetch(`${API_URL}/api/maintenance/equipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const newEquip = await response.json();
        setEquipment(prev => [...prev, newEquip]);
        setShowCreateDialog(false);
        setFormData({
          equipment_id: '',
          equipment_name: '',
          equipment_type: 'punch',
          max_cycles_before_inspection: 100000,
          max_cycles_before_replacement: 500000,
        });
        alert('Equipment registered successfully!');
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        try {
          const err = JSON.parse(errorText);
          alert(`Error ${response.status}: ${err.detail || err.message || errorText}`);
        } catch {
          alert(`Error ${response.status}: ${errorText.substring(0, 300)}`);
        }
      }
    } catch (err) {
      console.error('Fetch error:', err);
      alert(`Network error: ${err.message}`);
    }
  };
  
  const handleDelete = async (equipmentId) => {
    if (!confirm('Are you sure you want to delete this equipment? All maintenance history will be preserved.')) {
      return;
    }
    
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('DELETE', `${API_URL}/api/maintenance/equipment/${equipmentId}`, false);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send();
      
      if (xhr.status >= 200 && xhr.status < 300) {
        setEquipment(prev => prev.filter(e => e.equipment_id !== equipmentId));
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };
  
  const viewHistory = async (equip) => {
    setSelectedEquipment(equip);
    setShowHistoryDialog(true);
    
    try {
      // Fetch history
      const histXhr = new XMLHttpRequest();
      histXhr.open('GET', `${API_URL}/api/maintenance/log/equipment/${equip.equipment_id}`, false);
      histXhr.setRequestHeader('Authorization', `Bearer ${token}`);
      histXhr.send();
      
      if (histXhr.status >= 200 && histXhr.status < 300) {
        setHistory(JSON.parse(histXhr.responseText));
      }
      
      // Fetch predictions
      const predXhr = new XMLHttpRequest();
      predXhr.open('GET', `${API_URL}/api/maintenance/predictions/${equip.equipment_id}`, false);
      predXhr.setRequestHeader('Authorization', `Bearer ${token}`);
      predXhr.send();
      
      if (predXhr.status >= 200 && predXhr.status < 300) {
        setPrediction(JSON.parse(predXhr.responseText));
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };
  
  const copyNfcUrl = (equipmentId) => {
    const url = `${window.location.origin}/maintenance/${equipmentId}`;
    navigator.clipboard.writeText(url);
    alert(`Copied NFC URL:\n${url}`);
  };
  
  const getRiskBadge = (level) => {
    const colors = {
      low: 'bg-green-500',
      medium: 'bg-yellow-500',
      high: 'bg-orange-500',
      critical: 'bg-red-500'
    };
    return <Badge className={`${colors[level] || 'bg-gray-500'} text-white text-xs`}>{level}</Badge>;
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <div className="fixed inset-y-0 right-0 w-96 bg-slate-900 border-l border-slate-700 shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Maintenance Equipment</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
        </div>
        
        {/* Content */}
        <ScrollArea className="flex-1 p-4">
          {loading ? (
            <div className="text-center text-slate-400 py-8">Loading...</div>
          ) : equipment.length === 0 ? (
            <div className="text-center py-8">
              <Wrench className="h-12 w-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 mb-4">No equipment registered yet</p>
              {isAdmin && (
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Register Equipment
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {equipment.map((equip) => (
                <Card key={equip.equipment_id} className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-medium text-white">{equip.equipment_name}</h3>
                        <p className="text-xs text-slate-400">ID: {equip.equipment_id}</p>
                      </div>
                      {getRiskBadge(equip.risk_level)}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-slate-700/50 rounded p-2 text-center">
                        <div className="text-lg font-bold text-blue-400">
                          {equip.current_total_cycles?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs text-slate-400">Cycles</div>
                      </div>
                      <div className="bg-slate-700/50 rounded p-2 text-center">
                        <div className="text-lg font-bold text-amber-400">
                          {equip.max_cycles_before_replacement > 0 
                            ? Math.round((equip.current_total_cycles / equip.max_cycles_before_replacement) * 100)
                            : 0}%
                        </div>
                        <div className="text-xs text-slate-400">Life Used</div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => viewHistory(equip)}
                      >
                        <History className="h-3 w-3 mr-1" />
                        History
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyNfcUrl(equip.equipment_id)}
                        title="Copy NFC URL"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(`/maintenance/${equip.equipment_id}`, '_blank')}
                        title="Open Form"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                      {isAdmin && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleDelete(equip.equipment_id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer */}
        {isAdmin && equipment.length > 0 && (
          <div className="p-4 border-t border-slate-700">
            <Button className="w-full" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Register New Equipment
            </Button>
          </div>
        )}
      </div>
      
      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Register New Equipment</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add equipment for maintenance tracking. The ID will be used in NFC tags.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Equipment ID *</Label>
              <Input
                value={formData.equipment_id}
                onChange={(e) => setFormData(prev => ({ ...prev, equipment_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                placeholder="e.g., punch-001"
                className="bg-slate-800 border-slate-600"
              />
              <p className="text-xs text-slate-400 mt-1">Used in NFC URL: /maintenance/{formData.equipment_id || 'your-id'}</p>
            </div>
            
            <div>
              <Label>Equipment Name *</Label>
              <Input
                value={formData.equipment_name}
                onChange={(e) => setFormData(prev => ({ ...prev, equipment_name: e.target.value }))}
                placeholder="e.g., Cold Rolling Punch #1"
                className="bg-slate-800 border-slate-600"
              />
            </div>
            
            <div>
              <Label>Equipment Type</Label>
              <Select 
                value={formData.equipment_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, equipment_type: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="punch">Punch</SelectItem>
                  <SelectItem value="die">Die</SelectItem>
                  <SelectItem value="roller">Roller</SelectItem>
                  <SelectItem value="press">Press</SelectItem>
                  <SelectItem value="motor">Motor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Inspection Threshold</Label>
                <Input
                  type="number"
                  value={formData.max_cycles_before_inspection}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_cycles_before_inspection: parseInt(e.target.value) || 0 }))}
                  className="bg-slate-800 border-slate-600"
                />
                <p className="text-xs text-slate-400 mt-1">cycles</p>
              </div>
              <div>
                <Label>Replacement Threshold</Label>
                <Input
                  type="number"
                  value={formData.max_cycles_before_replacement}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_cycles_before_replacement: parseInt(e.target.value) || 0 }))}
                  className="bg-slate-800 border-slate-600"
                />
                <p className="text-xs text-slate-400 mt-1">cycles</p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Register Equipment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {selectedEquipment?.equipment_name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Maintenance history and predictions
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            {/* Predictions */}
            {prediction && (
              <Card className="bg-slate-800 border-slate-700 mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Status & Predictions</span>
                    {getRiskBadge(prediction.risk_level)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-400">{prediction.current_cycles?.toLocaleString()}</div>
                      <div className="text-xs text-slate-400">Total Cycles</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-amber-400">{prediction.percent_life_used}%</div>
                      <div className="text-xs text-slate-400">Life Used</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-400">{prediction.cycles_remaining_to_inspection?.toLocaleString()}</div>
                      <div className="text-xs text-slate-400">To Inspection</div>
                    </div>
                  </div>
                  
                  {prediction.alert_message && (
                    <div className={`p-2 rounded text-sm ${
                      prediction.risk_level === 'critical' ? 'bg-red-500/20 text-red-300' :
                      prediction.risk_level === 'high' ? 'bg-orange-500/20 text-orange-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`}>
                      {prediction.alert_message}
                    </div>
                  )}
                  
                  {prediction.recommendations?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-400 mb-1">Recommendations:</p>
                      <ul className="text-sm text-slate-300 space-y-1">
                        {prediction.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <ChevronRight className="h-3 w-3 mt-1 text-slate-500" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* History */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-300">Maintenance Log</h4>
              {history.length === 0 ? (
                <p className="text-slate-400 text-sm py-4 text-center">No maintenance records yet</p>
              ) : (
                history.map((log) => (
                  <Card key={log.id} className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Badge variant="outline" className="text-xs">{log.maintenance_type}</Badge>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(log.logged_at).toLocaleDateString()} - {log.logged_by_name}
                          </p>
                        </div>
                        {log.total_cycles && (
                          <span className="text-blue-400 font-mono text-sm">{log.total_cycles.toLocaleString()} cycles</span>
                        )}
                      </div>
                      {log.work_performed && (
                        <p className="text-sm text-slate-300">{log.work_performed}</p>
                      )}
                      {log.issues_found && (
                        <p className="text-sm text-amber-300 mt-1">Issues: {log.issues_found}</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MaintenanceEquipmentPanel;
